/**
 * Production Data Hooks
 */

import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { productionService } from '@/integrations/sqlserver/productionService';
import { 
  ProductionData, 
  ProductionFilters, 
  DailyProductionData, 
  ProductionStats,
  PackagingData,
  PackagingStats
} from '@/types/production';

/**
 * Fetch raw production data
 */
export function useProductionData(
  filters: ProductionFilters,
  enabled: boolean = true
): UseQueryResult<ProductionData[], Error> {
  return useQuery({
    queryKey: ['production-data', filters],
    queryFn: () => productionService.getProductionData(filters),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Get formatted daily production data
 */
export function useDailyProductionData(
  filters: ProductionFilters,
  enabled: boolean = true
): UseQueryResult<DailyProductionData[], Error> {
  return useQuery({
    queryKey: ['daily-production-data', filters],
    queryFn: async () => {
      const rawData = await productionService.getProductionData(filters);
      return productionService.transformToDaily(rawData);
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Get production statistics
 */
export function useProductionStats(
  filters: ProductionFilters,
  enabled: boolean = true
): UseQueryResult<ProductionStats, Error> {
  return useQuery({
    queryKey: ['production-stats', filters],
    queryFn: async () => {
      const rawData = await productionService.getProductionData(filters);
      return productionService.calculateStats(rawData);
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Get chart data for trends
 */
export function useProductionChartData(
  filters: ProductionFilters,
  processType: 'labor' | 'volume' | 'productivity',
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['production-chart-data', filters, processType],
    queryFn: async () => {
      const rawData = await productionService.getProductionData(filters);
      return productionService.getChartData(rawData, processType);
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Get packaging data for charts (includes all days, even zeros)
 */
export function usePackagingData(
  filters: ProductionFilters,
  enabled: boolean = true
): UseQueryResult<PackagingData[], Error> {
  return useQuery({
    queryKey: ['packaging-data', filters],
    queryFn: async () => {
      const rawData = await productionService.getProductionData(filters);
      
      // Return ALL days for chart display (chart will handle filtering for trendlines)
      const transformedData = rawData.map(item => ({
        date: item.DocDate,
        labor: item.CDGoi || 0,
        volumeCarton: item.M3DGoi || 0,
        volumeWood: item.M3WoodDGoi || 0,
        productivityCarton: item.NSDGoi || 0,
        productivityWood: item.NSWoodDGoi || 0,
        containerCount: item.QtyContDGoi || 0,
      }));
      
      console.log('📦 Packaging data (all days for chart):', transformedData.length, 'days');
      
      return transformedData;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Get packaging data for table (working days only)
 */
export function usePackagingTableData(
  filters: ProductionFilters,
  enabled: boolean = true
): UseQueryResult<PackagingData[], Error> {
  return useQuery({
    queryKey: ['packaging-table-data', filters],
    queryFn: async () => {
      const rawData = await productionService.getProductionData(filters);
      
      // Show ALL data in table (including rows with all zeros)
      // User requested to display all days, even those with no packaging activity
      const transformedData = rawData.map(item => ({
        date: item.DocDate,
        labor: item.CDGoi,
        volumeCarton: item.M3DGoi,
        volumeWood: item.M3WoodDGoi,
        productivityCarton: item.NSDGoi,
        productivityWood: item.NSWoodDGoi,
        containerCount: item.QtyContDGoi,
      }));
      
      console.log('📦 Packaging table data (tất cả các ngày):', transformedData.length, 'days displayed');
      console.log('📦 Showing all data including zero-value days as requested');
      
      return transformedData;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Get packaging statistics
 */
export function usePackagingStats(
  filters: ProductionFilters,
  enabled: boolean = true
): UseQueryResult<PackagingStats, Error> {
  return useQuery({
    queryKey: ['packaging-stats', filters],
    queryFn: async () => {
      const rawData = await productionService.getProductionData(filters);
      
      // Debug: Kiểm tra raw data structure
      console.log('🔍 Raw data sample for packaging stats:', rawData[0]);
      console.log('🔍 Available fields:', Object.keys(rawData[0] || {}));
      console.log('🔍 Packaging-specific fields:', {
        M3WoodDGoi: rawData[0]?.M3WoodDGoi,
        NSWoodDGoi: rawData[0]?.NSWoodDGoi,
        QtyContDGoi: rawData[0]?.QtyContDGoi,
        CDGoi: rawData[0]?.CDGoi,
        M3DGoi: rawData[0]?.M3DGoi,
        NSDGoi: rawData[0]?.NSDGoi
      });
      
      // Total days = all days (including days with all zeros)
      const totalDays = rawData.length;
      
      // Working days = days with at least one value > 0 (for average calculation)
      const workingDaysData = rawData.filter(item => 
        (item.CDGoi || 0) > 0 || 
        (item.M3DGoi || 0) > 0 || 
        (item.M3WoodDGoi || 0) > 0 || 
        (item.NSDGoi || 0) > 0 || 
        (item.NSWoodDGoi || 0) > 0 || 
        (item.QtyContDGoi || 0) > 0
      );

      console.log('🔍 Total days:', totalDays, '| Working days (> 0):', workingDaysData.length);

      if (workingDaysData.length === 0) {
        return {
          totalDays: totalDays,
          totalLabor: 0,
          avgVolumeCarton: 0,
          avgVolumeWood: 0,
          avgProductivityCarton: 0,
          avgProductivityWood: 0,
          avgContainerCount: 0,
          totalContainers: 0,
        };
      }

      // Calculate totals from working days only
      const totalLabor = workingDaysData.reduce((sum, item) => sum + (item.CDGoi || 0), 0);
      const totalContainers = workingDaysData.reduce((sum, item) => sum + (item.QtyContDGoi || 0), 0);
      
      // Calculate averages based on working days only (not total days)
      const avgVolumeCarton = workingDaysData.reduce((sum, item) => sum + (item.M3DGoi || 0), 0) / workingDaysData.length;
      const avgVolumeWood = workingDaysData.reduce((sum, item) => sum + (item.M3WoodDGoi || 0), 0) / workingDaysData.length;
      const avgProductivityCarton = workingDaysData.reduce((sum, item) => sum + (item.NSDGoi || 0), 0) / workingDaysData.length;
      const avgProductivityWood = workingDaysData.reduce((sum, item) => sum + (item.NSWoodDGoi || 0), 0) / workingDaysData.length;
      const avgContainerCount = totalContainers / workingDaysData.length;

      console.log('🔍 Calculation details:', {
        totalLabor,
        avgVolumeWood,
        avgProductivityWood,
        totalContainers,
        avgContainerCount
      });

      const result = {
        totalDays: totalDays, // All days (including zero days)
        totalLabor: Number(totalLabor.toFixed(1)),
        avgVolumeCarton: Number(avgVolumeCarton.toFixed(2)),
        avgVolumeWood: Number(avgVolumeWood.toFixed(2)),
        avgProductivityCarton: Number(avgProductivityCarton.toFixed(3)),
        avgProductivityWood: Number(avgProductivityWood.toFixed(3)),
        avgContainerCount: Number(avgContainerCount.toFixed(1)),
        totalContainers: Number(totalContainers.toFixed(0)),
      };
      
      console.log('🔍 Final packaging stats result:', result);
      return result;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to invalidate production queries
 */
export function useProductionCache() {
  const queryClient = useQueryClient();
  
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['production-data'] });
    queryClient.invalidateQueries({ queryKey: ['daily-production-data'] });
    queryClient.invalidateQueries({ queryKey: ['production-stats'] });
    queryClient.invalidateQueries({ queryKey: ['production-chart-data'] });
    queryClient.invalidateQueries({ queryKey: ['packaging-data'] });
    queryClient.invalidateQueries({ queryKey: ['packaging-table-data'] });
    queryClient.invalidateQueries({ queryKey: ['packaging-stats'] });
  };

  return { invalidateAll };
}